import React, { useCallback, useEffect, useState } from 'react';
import BottomSlot from './BottomSlot';
import { useWorldStore } from '../state/worldStore';
import { seatBridge } from '../state/seatBridge';
import useRoomSeats from '../hooks/useRoomSeats';
import useListeningPlayback from '../hooks/useListeningPlayback';
import * as LR from '../services/listening';
import { api } from '../services/api';
import { ROOMS } from '../rooms';

// ListeningDock — open a listening room in the room you are standing in.
//
// Khari, 2026-08-04: "inviting on the phone is fine, but implies you'd be
// planning it instead of initiating it. that is fine only if you can initiate it
// from within the room itself (which is where most first time users will look to
// find the feature. looking in the phone comes after more experience)."
//
// So this is the FRONT DOOR and the phone is the later path. It appears only
// where it can work — a room with seats — because an action that is present
// everywhere and fails in most places teaches people to ignore it.
//
// It lives in BottomSlot rather than picking its own `position: fixed`, which is
// the rule that stopped the HUD stacking on itself. Order 30 puts it between the
// scene dock (20) and the live-chat chip (40).

const chip = {
  display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'auto',
  padding: '8px 13px', borderRadius: 999, cursor: 'pointer', border: '1px solid rgba(255,178,94,0.45)',
  background: 'rgba(14,9,6,0.92)', color: '#ffe9c4', font: '600 12.5px system-ui',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
};
const panel = {
  ...chip, flexDirection: 'column', alignItems: 'stretch', gap: 7, cursor: 'default',
  borderRadius: 14, padding: 12, width: 'min(320px, 88vw)',
};
const btn = (primary = true) => ({
  padding: '7px 11px', borderRadius: 9, cursor: 'pointer', font: '700 12px system-ui',
  border: primary ? 'none' : '1px solid rgba(255,178,94,0.4)',
  background: primary ? '#ffb25e' : 'transparent', color: primary ? '#1c1206' : '#ffe9c4',
});
const field = {
  flex: 1, minWidth: 0, padding: '7px 9px', borderRadius: 8, fontSize: 12.5,
  border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none',
};

export default function ListeningDock() {
  const interior = useWorldStore((s) => s.interior);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  // The server's view of the room, pushed over the world socket. Held in the
  // store rather than subscribed to here so the seated BODIES read the same
  // object this panel does — one truth, or the chairs and the list disagree.
  const room = useWorldStore((s) => s.listeningRoom);
  const setRoom = useWorldStore((s) => s.setListeningRoom);
  const invited = useWorldStore((s) => s.listeningInvite);
  const clearInvite = useWorldStore((s) => s.clearListeningInvite);
  // HOW MANY CHAIRS ARE ACTUALLY IN HERE — the room's own furniture, which is
  // what a listening room's capacity IS (project_world_listening_rooms). Khari,
  // 2026-08-04: "limit the number so it is an intimate event... what the room
  // can hold is better and will give more options."
  //
  // Shared with the 3D layer, and it must be: the server hands out seat INDICES,
  // so the count offered here and the chairs drawn there have to come from the
  // same ordered list. See hooks/useRoomSeats.
  const seatList = useRoomSeats(interior);
  const seats = seatList.length;
  // Same rule the 3D layer uses (ListeningSeats): a `firstPerson` room seats
  // your CAMERA, an orbit room seats your AVATAR and leaves the view alone.
  // Read from the room rather than from seatBridge — that bridge is a plain
  // module object on purpose ("nothing should re-render because a chair became
  // available"), so rendering off it would race the 3D layer's first publish.
  const thirdPerson = !ROOMS[interior]?.firstPerson;
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState('');
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // WHO IS HOSTING IS THE SERVER'S ANSWER, not a local flag.
  //
  // This was a ref set at open() — which survives exactly until a refresh, at
  // which point the host silently loses the record picker while the server
  // still happily accepts their now-playing writes. Derived from the room state
  // instead, so re-entering by code, reloading, or arriving from a deep link all
  // land on the same truth.
  const isHost = !!(room && myAvatarId && room.hostAvatarId === myAvatarId);

  // Whether you are actually IN the chair right now. `lr:` is the target prefix
  // the 3D layer seats you with — anything else means you are sitting in
  // something the room itself offered, which this button has no business
  // standing you out of.
  const seated = useWorldStore((s) => s.seated);
  const standUp = useWorldStore((s) => s.standUp);
  const inMySeat = !!(seated && typeof seated.target === 'string' && seated.target.startsWith('lr:'));

  // Hearing what the host put on. Mounted here rather than in the 3D layer
  // because it must keep working with the panel closed — you sit down, close
  // this, and carry on listening.
  useListeningPlayback();

  // Rung while standing in the world: open with the code already filled, so
  // taking the seat is one press rather than a hunt. Consumed, so it does not
  // re-open every time this remounts.
  useEffect(() => {
    if (!invited?.code) return;
    setOpen(true);
    setCode(invited.code);
    setMsg(`${invited.from?.name || 'Someone'} saved you a seat — press Join`);
    clearInvite();
  }, [invited, clearInvite]);

  // A notification deep-links here with ?listen=CODE — but only if you happen
  // to be standing in a room with seats, which after tapping a push you are
  // almost certainly not. The always-mounted handler in App.jsx files it into
  // the Calls log instead; this just pre-fills the field for the case where you
  // ARE already in the right room.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('listen');
    if (q) { setOpen(true); setCode(q.toUpperCase()); }
  }, []);

  const refresh = useCallback(async (c) => {
    try { setRoom(await LR.roomByCode(c)); } catch { setRoom(null); }
  }, []);

  const doOpen = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await LR.openRoom({ zone: interior, seatCount: seats, avatarId: myAvatarId });
      setRoom(r);
      setMsg(`Room open — ${r.open} seat${r.open === 1 ? '' : 's'} to give away`);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const doInvite = async () => {
    if (!handle.trim() || !room) return;
    setBusy(true); setMsg('');
    try {
      const d = await LR.invite(room.roomId, handle.trim());
      setRoom(d.room || room);
      setHandle('');
      // Say whether they were actually reachable. "Invite sent" is the same
      // sentence whether it rang in their ear or landed in a list they will read
      // tomorrow, and the difference is the whole experience.
      setMsg(d.listeners
        ? `Ringing @${d.invited.handle} — they're in the world`
        : `Invited @${d.invited.handle} — they'll see it next time they're on`);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  // ── ASK A RESIDENT ────────────────────────────────────────────────────────
  //
  // Khari, 2026-08-04: "perhaps a 'resident' can join you if the user doesn't
  // respond. the avatar (if free) can join on its own."
  //
  // The server has always been able to do this; there was no way to ask. It
  // splits the question in two because each side can only answer half: WE know
  // which avatars have a rigged body (roster.json is an asset in this repo and
  // the server keeps no copy), the server knows which of those is free — being
  // on shift is a placement carrying `data.actor`, so "who is working right now"
  // is one query.
  //
  // `force: true` because this is a BUTTON. Unforced, the endpoint waits for an
  // invite to go stale — the right rule for a room filling itself in the
  // background, and the wrong one for a host who just decided the room should
  // have somebody in it. That automatic path would be a timer, and it does not
  // exist yet; this is the host saying so out loud.
  //
  // A filled seat is a BODY, not a voice. Nothing here costs anything to run.
  const doFill = async () => {
    if (!room) return;
    setBusy(true); setMsg('');
    try {
      const res = await fetch('/models/avatars/roster.json', { cache: 'no-store' });
      const d = await res.json();
      const candidates = (d.npcs || []).filter((n) => n.rigged && n.avatarId).map((n) => n.avatarId);
      if (!candidates.length) { setMsg('Nobody around here has a body yet'); return; }
      const out = await LR.fillSeat(room.roomId, candidates, { force: true });
      if (out.room) setRoom(out.room);
      // `why` is the server being specific about a no-op — everyone is on shift,
      // the room filled up first, nobody has a body. Showing it beats "nothing
      // happened", which is what a silent success looks like from here.
      setMsg(out.filled
        ? `${out.filled.name || 'A resident'} sat down — seat ${out.filled.seat + 1}`
        : `No one came — ${out.why || 'nobody free'}`);
    } catch (e) {
      setMsg(e.message);
    } finally { setBusy(false); }
  };

  const doJoin = async () => {
    if (!code.trim()) return;
    setBusy(true); setMsg('');
    try {
      const d = await LR.join(code.trim().toUpperCase(), myAvatarId);
      setRoom(d.room);
      setMsg(d.rejoined ? 'Back in your seat' : `You're in — seat ${d.seat + 1}`);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const doLeave = async () => {
    if (!room) return;
    setBusy(true);
    try { await LR.leave(room.roomId); } catch { /* leaving always works */ }
    setRoom(null); setMsg(''); setBusy(false);
  };

  // ── PUTTING SOMETHING ON ──────────────────────────────────────────────────
  //
  // Khari, 2026-08-04: guests "listen to the playlists and other shared
  // selections of the person who opened the room."
  //
  // The host's OWN catalogue is the source, which is the whole shape of the
  // feature: you invite people into your room to play them your records. Loaded
  // once when the picker is first opened rather than on mount — most visits to
  // this dock never put anything on.
  const [tracks, setTracks] = useState(null);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (!picking || tracks || !myAvatarId) return;
    api.avatarTracks(myAvatarId)
      .then((d) => setTracks((d?.tracks || []).filter((t) => t.audio_url)))
      .catch(() => setTracks([]));
  }, [picking, tracks, myAvatarId]);

  const putOn = async (t) => {
    if (!room) return;
    setBusy(true);
    try {
      await LR.setNowPlaying(room.roomId, {
        trackId: t.track_id,
        title: t.title,
        artist: t.avatar_name || null,
        artworkUrl: t.artwork_url || null,
        audioUrl: t.audio_url,
        // Lets a late arrival tell "halfway through" from "already finished" —
        // without it they would seek past the end of a track nobody is playing.
        durationSec: t.duration_seconds || t.duration || null,
      });
      setPicking(false);
      setMsg(`Playing "${t.title}" for the room`);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const copyCode = () => {
    if (!room) return;
    navigator.clipboard?.writeText(room.code).then(
      () => setMsg('Code copied'),
      () => setMsg(`Your code is ${room.code}`),
    );
  };

  // Nothing to offer in a room with no furniture, and nothing to offer outdoors.
  if (!interior || !seats) return null;

  const roomName = ROOMS[interior]?.name || interior;

  if (!open) {
    return (
      <BottomSlot order={30}>
        <button style={chip} onClick={() => { setOpen(true); refresh(code); }}>
          🎧 Listen together
          <span style={{ opacity: 0.6, fontWeight: 400 }}>{seats} seats</span>
        </button>
      </BottomSlot>
    );
  }

  return (
    <BottomSlot order={30}>
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <b style={{ fontSize: 12.5 }}>🎧 Listening room · {roomName}</b>
          <button style={{ ...btn(false), padding: '3px 8px' }} onClick={() => setOpen(false)}>×</button>
        </div>

        {!room && (
          <>
            <div style={{ fontSize: 11, opacity: 0.72, lineHeight: 1.45 }}>
              {seats} seat{seats === 1 ? '' : 's'} in here — open it and {seats - 1} other
              {seats === 2 ? '' : 's'} can sit down and hear what you play.
            </div>
            <button style={btn()} disabled={busy} onClick={doOpen}>
              {busy ? '…' : 'Open the room'}
            </button>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                style={field} value={code} placeholder="or join with a code"
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && doJoin()}
              />
              <button style={btn(false)} disabled={busy || !code.trim()} onClick={doJoin}>Join</button>
            </div>
          </>
        )}

        {room && (
          <>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button style={{ ...btn(false), letterSpacing: 3, fontSize: 15 }} onClick={copyCode}>{room.code}</button>
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                {room.capacity - room.open}/{room.capacity} seated
              </span>
            </div>

            {isHost && room.open > 0 && (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  style={field} value={handle} placeholder="invite @handle"
                  onChange={(e) => setHandle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doInvite()}
                />
                <button style={btn()} disabled={busy || !handle.trim()} onClick={doInvite}>Ask</button>
              </div>
            )}

            {/* The other way to fill a chair — somebody who already lives here.
                Sits UNDER the invite because it is the fallback, not the plan
                (Khari: "if the user doesn't respond"), and only shows while
                there is a chair to fill. */}
            {isHost && room.open > 0 && (
              <button
                style={{ ...btn(false), fontSize: 11.5, alignSelf: 'flex-start' }}
                disabled={busy}
                onClick={doFill}
                title="Someone who lives around here takes an empty chair"
              >
                + Ask a resident
              </button>
            )}

            {/* WHO IS IN THE CHAIRS. Empty ones are drawn as empty rather than
                omitted — an intimate room is partly about the seats you can see
                are still free. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {room.seats.map((s, i) => (
                <span key={i} style={{
                  padding: '3px 8px', borderRadius: 999, fontSize: 10.5,
                  border: '1px solid rgba(255,178,94,0.28)',
                  background: s ? 'rgba(255,178,94,0.16)' : 'transparent',
                  opacity: s ? 1 : 0.45,
                }}>
                  {s ? `${s.name || 'guest'}${s.host ? ' ★' : ''}${s.npc ? ' ·npc' : ''}` : 'empty'}
                </span>
              ))}
            </div>

            {room.nowPlaying && (
              <div style={{ fontSize: 11, opacity: 0.8 }}>
                ♪ {room.nowPlaying.title || `track ${room.nowPlaying.trackId}`}
                {room.nowPlaying.artist ? ` — ${room.nowPlaying.artist}` : ''}
              </div>
            )}

            {/* THE HOST'S RECORDS. Only the host sees this — everyone else is
                here to listen, and the one permission that decides it is the
                same one that widens when co-hosting arrives. */}
            {isHost && (
              picking ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 168, overflowY: 'auto' }}>
                  {tracks === null && <div style={{ fontSize: 11, opacity: 0.6 }}>Getting your records…</div>}
                  {tracks?.length === 0 && (
                    <div style={{ fontSize: 11, opacity: 0.6, lineHeight: 1.4 }}>
                      Nothing released yet — cut a track and it can play in here.
                    </div>
                  )}
                  {(tracks || []).map((t) => (
                    <button
                      key={t.track_id} disabled={busy} onClick={() => putOn(t)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left',
                        padding: '5px 7px', borderRadius: 8, cursor: 'pointer',
                        border: '1px solid rgba(255,178,94,0.22)', background: 'transparent',
                        color: '#ffe9c4', font: '600 11.5px system-ui',
                      }}
                    >
                      {t.artwork_url
                        ? <img src={t.artwork_url} alt="" width={22} height={22} style={{ borderRadius: 4, objectFit: 'cover' }} />
                        : <span style={{ width: 22, textAlign: 'center' }}>♪</span>}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                    </button>
                  ))}
                  <button style={{ ...btn(false), fontSize: 11 }} onClick={() => setPicking(false)}>Close</button>
                </div>
              ) : (
                <button style={btn()} disabled={busy} onClick={() => setPicking(true)}>
                  {room.nowPlaying ? 'Put on something else' : 'Put on a record'}
                </button>
              )
            )}

            {/* IN THE CHAIR, OR ON YOUR FEET. Joining sits you down — that is
                what a listening room is — but standing to look at the sleeves,
                talk to somebody or move the TV up is normal, and nothing else in
                the world offers a way out of THIS seat (the sit markers belong
                to marked furniture, which a splat room has none of). So the way
                back in has to live next to the way out. */}
            {/* Only where sitting MOVES you. In a third-person room your avatar
                takes the chair and the camera stays on orbit (Khari, 2026-08-07:
                "let me see myself... people will be able to see their avatars in
                3rd person"), so there is no seat to stand out of — and a button
                offering it would either do nothing or snatch the view. */}
            {!thirdPerson && (
            <button
              style={{ ...btn(false), fontSize: 11 }}
              onClick={() => (inMySeat ? standUp() : seatBridge.sit?.())}
            >
              {inMySeat ? 'Stand up' : 'Take my seat'}
            </button>
            )}

            <button style={{ ...btn(false), fontSize: 11 }} disabled={busy} onClick={doLeave}>
              {isHost ? 'Close my seat' : 'Leave'}
            </button>
          </>
        )}

        {msg && <div style={{ fontSize: 10.5, opacity: 0.85, color: msg.includes('No ') || msg.includes('full') ? '#ff9d7d' : '#9dff7d' }}>{msg}</div>}
      </div>
    </BottomSlot>
  );
}
